/**
 * Pipedrive Activities Page
 * List of activities with search, filtering, and pagination
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { usePipedriveActivities, usePipedriveActivityFilters } from '@/hooks/use-pipedrive-api'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Calendar,
  Search,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  CheckCircle,
  Clock,
} from 'lucide-react'
import { GRACKLE_COLORS, SEMANTIC } from '@/lib/theme'
import type { PipedriveActivity } from '@/types/pipedrive'

export default function PipedriveActivities() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [type, setType] = useState('')
  const [done, setDone] = useState('')
  const limit = 25

  const { data, isLoading, error } = usePipedriveActivities({
    page,
    limit,
    search,
    type: type || undefined,
    done: done || undefined,
  })

  const { data: filters } = usePipedriveActivityFilters()

  const activities = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = data?.pages ?? 1

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const clearFilters = () => {
    setSearch('')
    setType('')
    setDone('')
    setPage(1)
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Activities</h1>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Failed to load activities. Please try again later.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-6 w-6" aria-hidden="true" />
            Activities
          </h1>
          <p className="text-muted-foreground">{total.toLocaleString()} activities</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search activities..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9"
                  aria-label="Search activities"
                />
              </div>
            </div>
            <Select
              value={type || '__all__'}
              onValueChange={(v) => {
                setType(v === '__all__' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[150px]" aria-label="Filter by type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Types</SelectItem>
                {filters?.types?.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={done || '__all__'}
              onValueChange={(v) => {
                setDone(v === '__all__' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[150px]" aria-label="Filter by status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {filters?.doneOptions?.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d === 'Yes' ? 'Completed' : 'Pending'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Subject</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Deal</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Due Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-40" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-6 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  </TableRow>
                ))
              ) : activities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No activities found
                  </TableCell>
                </TableRow>
              ) : (
                activities.map((activity: PipedriveActivity) => (
                  <TableRow key={activity.id}>
                    <TableCell className="font-medium">
                      <Link
                        to={`/pipedrive/activities/${activity.id}`}
                        className="text-primary hover:underline"
                      >
                        {activity.subject || '-'}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{activity.type || 'Unknown'}</Badge>
                    </TableCell>
                    <TableCell>
                      {activity.organization_id ? (
                        <Link
                          to={`/pipedrive/organizations/${activity.organization_id}`}
                          className="text-primary hover:underline"
                        >
                          {activity.organization || '-'}
                        </Link>
                      ) : (
                        activity.organization || '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {activity.deal_id ? (
                        <Link
                          to={`/pipedrive/deals/${activity.deal_id}`}
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          {activity.deal || '-'}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : (
                        activity.deal || '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {activity.done === 'Yes' ? (
                          <>
                            <CheckCircle
                              className="h-4 w-4"
                              style={{ color: GRACKLE_COLORS.prussianBlue }}
                              aria-hidden="true"
                            />
                            <span
                              style={{
                                color: GRACKLE_COLORS.prussianBlue,
                                backgroundColor: SEMANTIC.success.fill,
                                padding: '2px 8px',
                                borderRadius: '4px',
                              }}
                            >
                              Done
                            </span>
                          </>
                        ) : (
                          <>
                            <Clock
                              className="h-4 w-4"
                              style={{ color: GRACKLE_COLORS.prussianBlue }}
                              aria-hidden="true"
                            />
                            <span
                              style={{
                                color: GRACKLE_COLORS.prussianBlue,
                                backgroundColor: SEMANTIC.warning.fill,
                                padding: '2px 8px',
                                borderRadius: '4px',
                              }}
                            >
                              Pending
                            </span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{activity.due_date || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of{' '}
            {total.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
